import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Chip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "SET_TAGS") {
    return action.payload;
  }

  if (action.type === "LOAD_TAGS") {
    return action.payload;
  }

  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tag.id);

    if (tagIndex !== -1) {
      const nextState = [...state];
      nextState[tagIndex] = tag;
      return nextState;
    }

    return [...state, tag];
  }

  if (action.type === "DELETE_TAGS") {
    const tagId = action.payload;
    return state.filter((tag) => tag.id !== tagId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  dragHandle: {
    cursor: "grab",
    color: theme.palette.text.secondary,
  },
  draggingRow: {
    opacity: 0.55,
  },
  dropTargetRow: {
    backgroundColor:
      theme.palette.type === "light"
        ? "rgba(25, 118, 210, 0.08)"
        : "rgba(144, 202, 249, 0.12)",
  },
  orderCell: {
    whiteSpace: "nowrap",
  },
  orderHint: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
}));

const TagsKanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  const canReorder = !searchParam && tags.length > 1;

  const fetchTags = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tags/", {
        params: {
          searchParam,
          pageNumber: 1,
          kanban: 1,
          all: true,
        },
      });
      dispatch({ type: "LOAD_TAGS", payload: data.tags });
      setOrderDirty(false);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchTags();
    }, searchParam ? 500 : 0);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    const onTagsEvent = (data) => {
      if (data.action === "reorder") {
        dispatch({ type: "LOAD_TAGS", payload: data.tags });
        setOrderDirty(false);
        return;
      }

      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };

    socket.on(`company${user.companyId}-tag`, onTagsEvent);

    return () => {
      socket.off(`company${user.companyId}-tag`, onTagsEvent);
    };
  }, [socket, user.companyId]);

  const reorderTags = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= tags.length || toIndex >= tags.length) return;

    const nextTags = [...tags];
    const [movedTag] = nextTags.splice(fromIndex, 1);
    nextTags.splice(toIndex, 0, movedTag);
    dispatch({ type: "SET_TAGS", payload: nextTags });
    setOrderDirty(true);
  };

  const handleMoveUp = (index) => {
    reorderTags(index, index - 1);
  };

  const handleMoveDown = (index) => {
    reorderTags(index, index + 1);
  };

  const handleDragStart = (index) => {
    if (!canReorder) return;
    setDraggedIndex(index);
  };

  const handleDragOver = (event, index) => {
    if (!canReorder || draggedIndex === null) return;
    event.preventDefault();
    setDropIndex(index);
  };

  const handleDrop = (index) => {
    if (!canReorder || draggedIndex === null) return;
    reorderTags(draggedIndex, index);
    setDraggedIndex(null);
    setDropIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropIndex(null);
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      const { data } = await api.put("/tag/kanban/reorder", {
        tagIds: tags.map((tag) => tag.id),
      });
      dispatch({ type: "LOAD_TAGS", payload: data.tags });
      setOrderDirty(false);
      toast.success(i18n.t("tagsKanban.toasts.orderSaved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingOrder(false);
    }
  };

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
    fetchTags();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
    setOrderDirty(false);
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tagsKanban.toasts.deleted"));
      fetchTags();
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
  };

  const handleReturnToKanban = () => {
    history.push("/kanban");
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingTag && `${i18n.t("tagsKanban.confirmationModal.deleteTitle")}`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tagsKanban.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {tagModalOpen && (
        <TagModal
          open={tagModalOpen}
          onClose={handleCloseTagModal}
          aria-labelledby="form-dialog-title"
          tagId={selectedTag && selectedTag.id}
          kanban={1}
        />
      )}
      <MainHeader>
        <Title>{i18n.t("tagsKanban.title")} ({tags.length})</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          {canReorder && (
            <Button
              variant="contained"
              color="default"
              disabled={!orderDirty || savingOrder}
              onClick={handleSaveOrder}
            >
              {i18n.t("tagsKanban.buttons.saveOrder")}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenTagModal}
          >
            {i18n.t("tagsKanban.buttons.add")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReturnToKanban}
          >
            {"Volver al Kanban"}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {canReorder && (
          <div className={classes.orderHint}>
            {i18n.t("tagsKanban.buttons.dragHint")}. Usa las flechas o arrastra las filas, luego pulsa «{i18n.t("tagsKanban.buttons.saveOrder")}».
          </div>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              {canReorder && <TableCell align="center" />}
              <TableCell align="center">{i18n.t("tagsKanban.table.order")}</TableCell>
              <TableCell align="center">{i18n.t("tagsKanban.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("tagsKanban.table.tickets")}</TableCell>
              <TableCell align="center">{i18n.t("tagsKanban.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {tags.map((tag, index) => (
                <TableRow
                  key={tag.id}
                  draggable={canReorder}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(event) => handleDragOver(event, index)}
                  onDrop={() => handleDrop(index)}
                  onDragEnd={handleDragEnd}
                  className={[
                    draggedIndex === index ? classes.draggingRow : "",
                    dropIndex === index && draggedIndex !== index ? classes.dropTargetRow : "",
                  ].join(" ")}
                >
                  {canReorder && (
                    <TableCell align="center" className={classes.orderCell}>
                      <DragIndicatorIcon className={classes.dragHandle} fontSize="small" />
                    </TableCell>
                  )}
                  <TableCell align="center" className={classes.orderCell}>
                    <IconButton
                      size="small"
                      disabled={!canReorder || index === 0}
                      onClick={() => handleMoveUp(index)}
                      title={i18n.t("tagsKanban.buttons.moveUp")}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <span>{index + 1}</span>
                    <IconButton
                      size="small"
                      disabled={!canReorder || index === tags.length - 1}
                      onClick={() => handleMoveDown(index)}
                      title={i18n.t("tagsKanban.buttons.moveDown")}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      variant="outlined"
                      style={{
                        backgroundColor: tag.color,
                        textShadow: "1px 1px 1px #000",
                        color: "white",
                      }}
                      label={tag.name}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {tag?.ticketTags ? tag.ticketTags.length : 0}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEditTag(tag)}>
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingTag(tag);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={canReorder ? 5 : 4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default TagsKanban;
